import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Group as GroupIcon, 
  People as PeopleIcon,
  Visibility as ViewIcon 
} from '@mui/icons-material';
import { NotificationLevel } from './BulkNotificationModal';

export interface BulkNotificationItem {
  id: number;
  createdAt: string;
  message: string;
  level: NotificationLevel;
  targetType: 'all' | 'groups';
  targetGroupId?: number | null;
  groupNames?: string[];
  sentCount: number;
}

const LEVEL_LABELS: Record<NotificationLevel, string> = {
  info: 'Інфо',
  warning: 'Увага',
  danger: 'Важливо',
};

const LEVEL_COLORS: Record<NotificationLevel, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  danger: 'error',
};

interface BulkNotificationsTableProps {
  notifications: BulkNotificationItem[];
  onViewDetails: (item: BulkNotificationItem) => void;
}

export const BulkNotificationsTable: React.FC<BulkNotificationsTableProps> = ({ 
  notifications,
  onViewDetails 
}) => {
  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Дата та час</TableCell>
            <TableCell>Аудиторія</TableCell>
            <TableCell>Повідомлення</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell align="right">Охоплення</TableCell>
            <TableCell align="center" sx={{ width: 80 }}>Дії</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notifications.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  Історія масових розсилок порожня.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            notifications.map((n) => (
              <TableRow 
                hover 
                key={n.id} 
                onClick={() => onViewDetails(n)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">
                    {new Date(n.createdAt).toLocaleDateString('uk-UA')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(n.createdAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {n.targetType === 'all' ? (
                      <Chip 
                        icon={<PeopleIcon />} 
                        label="Всі клієнти" 
                        size="small" 
                        variant="outlined" 
                        sx={{ borderRadius: 1 }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <GroupIcon fontSize="small" color="action" />
                        {n.groupNames?.map((name) => (
                          <Chip 
                            key={name} 
                            label={name} 
                            size="small" 
                            variant="outlined" 
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography variant="body2" sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {n.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={LEVEL_LABELS[n.level]}
                    color={LEVEL_COLORS[n.level]}
                    size="small"
                    sx={{ minWidth: 70 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {n.sentCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    клієнтів
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Переглянути деталі">
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(n);
                    }}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
